/** Client keys for Architecture assistant chat (EnhancedAIAgentV2). */
export const EA_THREAD_KEY = "ea_archimedes_thread_id";
export const EA_CONTEXT_KEY = "ea_archimedes_conversation_context";
/** Last in-memory Ask messages for the active thread (survives route remount). */
export const EA_THREAD_SNAPSHOT_KEY = "ea_archimedes_thread_snapshot";

/**
 * Drop local thread/context (logout, new login, admin wipe).
 * @param {{ emitEvent?: boolean }} [opts] When emitEvent is true (default),
 *   mounted Ask UIs reset. Pass false on logout so we don't create a new
 *   server thread while the session is already gone.
 */
export function clearAgentChatClientState(opts = {}) {
  const emitEvent = opts.emitEvent !== false;
  try {
    sessionStorage.removeItem(EA_THREAD_KEY);
    sessionStorage.removeItem(EA_CONTEXT_KEY);
    sessionStorage.removeItem(EA_THREAD_SNAPSHOT_KEY);
  } catch {
    /* ignore */
  }
  if (!emitEvent) return;
  try {
    window.dispatchEvent(new CustomEvent("dp-agent-chat-cleared"));
  } catch {
    /* ignore */
  }
}

export function readStoredAgentThreadId() {
  try {
    return String(sessionStorage.getItem(EA_THREAD_KEY) || "").trim();
  } catch {
    return "";
  }
}

/** Keep only fields needed to restore follow-up chips after Ask remount. */
function slimMessageMetadata(meta) {
  if (!meta || typeof meta !== "object") return {};
  const out = {};
  if (Array.isArray(meta.suggested_actions) && meta.suggested_actions.length) {
    out.suggested_actions = meta.suggested_actions.map((a) => {
      if (!a || typeof a !== "object") return a;
      return {
        id: a.id,
        label: a.label,
        message: a.message,
        entity_id: a.entity_id,
        entity_name: a.entity_name,
        entity_type: a.entity_type,
        ask_shape: a.ask_shape,
        expand_channel: a.expand_channel,
        section_key: a.section_key,
        depth: a.depth,
        verified_ask_id: a.verified_ask_id,
      };
    });
  }
  if (meta.turn_id) out.turn_id = meta.turn_id;
  if (meta.query_focus_entity) out.query_focus_entity = meta.query_focus_entity;
  return out;
}

function contentKey(content) {
  return String(content || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 240);
}

/**
 * Persist a slim message snapshot so follow-up chips survive Ask remount
 * before (or without) server metadata.
 * @param {string} threadId
 * @param {Array<object>} messages
 */
export function writeAgentThreadSnapshot(threadId, messages) {
  const tid = String(threadId || "").trim();
  if (!tid || !Array.isArray(messages) || messages.length === 0) return;
  try {
    const slim = messages.slice(-40).map((m) => ({
      id: m.id,
      role: m.role,
      content: m.content,
      timestamp:
        m.timestamp instanceof Date
          ? m.timestamp.toISOString()
          : m.timestamp || null,
      metadata: slimMessageMetadata(m.metadata || {}),
    }));
    sessionStorage.setItem(
      EA_THREAD_SNAPSHOT_KEY,
      JSON.stringify({ threadId: tid, messages: slim, savedAt: Date.now() })
    );
  } catch {
    /* ignore quota / private mode */
  }
}

export function readAgentThreadSnapshot(threadId) {
  const tid = String(threadId || "").trim();
  if (!tid) return null;
  try {
    const raw = sessionStorage.getItem(EA_THREAD_SNAPSHOT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (String(parsed?.threadId || "").trim() !== tid) return null;
    return Array.isArray(parsed.messages) ? parsed.messages : null;
  } catch {
    return null;
  }
}

export function clearAgentThreadSnapshot() {
  try {
    sessionStorage.removeItem(EA_THREAD_SNAPSHOT_KEY);
  } catch {
    /* ignore */
  }
}

function snapshotMessagesToUi(snapshotMessages) {
  return (snapshotMessages || []).map((m, idx) => ({
    ...m,
    id: m.id || `snap-${idx}`,
    timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
    metadata: { ...(m.metadata || {}) },
  }));
}

/**
 * Fill missing suggested_actions on API-resumed messages from a live snapshot.
 * If the API returns no messages, keep the snapshot (do not wipe chips).
 */
export function mergeSuggestedActionsFromSnapshot(apiMessages, snapshotMessages) {
  const api = Array.isArray(apiMessages) ? apiMessages : [];
  const snap = Array.isArray(snapshotMessages) ? snapshotMessages : [];
  if (!snap.length) return api;
  if (!api.length) return snapshotMessagesToUi(snap);

  const byTurn = new Map();
  const byContent = new Map();
  let lastSnapActions = null;
  for (const m of snap) {
    if (m?.role !== "assistant") continue;
    const actions = m?.metadata?.suggested_actions;
    if (!Array.isArray(actions) || !actions.length) continue;
    lastSnapActions = actions;
    const turn = String(m?.metadata?.turn_id || "").trim();
    if (turn) byTurn.set(turn, actions);
    const key = contentKey(m?.content);
    if (key) byContent.set(key, actions);
  }
  if (!byTurn.size && !byContent.size && !lastSnapActions) return api;

  const merged = api.map((m) => {
    if (m?.role !== "assistant") return m;
    const existing = m?.metadata?.suggested_actions;
    if (Array.isArray(existing) && existing.length) return m;
    const turn = String(m?.metadata?.turn_id || "").trim();
    const key = contentKey(m?.content);
    const actions =
      (turn && byTurn.get(turn)) || (key && byContent.get(key)) || null;
    if (!actions) return m;
    return {
      ...m,
      metadata: {
        ...(m.metadata || {}),
        suggested_actions: actions,
      },
    };
  });

  // Last resort: copy chips onto the latest assistant turn when content/turn
  // keys diverged slightly between live UI and persisted message text.
  if (lastSnapActions) {
    for (let i = merged.length - 1; i >= 0; i -= 1) {
      const m = merged[i];
      if (m?.role !== "assistant") continue;
      const existing = m?.metadata?.suggested_actions;
      if (Array.isArray(existing) && existing.length) break;
      merged[i] = {
        ...m,
        metadata: {
          ...(m.metadata || {}),
          suggested_actions: lastSnapActions,
        },
      };
      break;
    }
  }

  return merged;
}
